import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { TRequestStatus } from "@models/msc.model";
import {
  assignConversation,
  getConversation,
  getConversations,
  getMessages,
  getUnreadCount,
  markConversationRead,
  sendImageReply,
  sendTextReply,
  setConversationStatus,
} from "@/services/inbox.service";
import { getErrorFromBackend } from "@/utils/errors";
import type {
  TConversationFilters,
  TInboxConversation,
  TInboxConversationStatus,
  TInboxMessage,
  TPendingReply,
} from "@/models/inbox.model";

type Thread = { messages: TInboxMessage[]; hasMore: boolean; status: TRequestStatus };

type InboxState = {
  conversations: TInboxConversation[];
  nextCursor?: string;
  listStatus: TRequestStatus;
  initialized: boolean;
  filters: TConversationFilters;
  /** Conversations opened from a link that the current list page doesn't hold. */
  extra: Record<number, TInboxConversation>;
  /** The open conversation; kept in `extra` when a refresh drops it from the list. */
  activeId?: number;
  threads: Record<number, Thread>;
  /** Replies not yet stored by the server, per conversation (IR-70). */
  pending: Record<number, TPendingReply[]>;
  unreadCount: number;
  /** Open conversations nobody is assigned to, company-wide (Inbox header). */
  unassignedCount: number;
};

type InboxActions = {
  setActive: (id: number | undefined) => void;
  setFilters: (patch: Partial<TConversationFilters>) => void;
  /** `silent` refreshes in the background (polling) without a loading state. */
  fetchConversations: (silent?: boolean) => Promise<void>;
  loadMoreConversations: () => Promise<void>;
  ensureConversation: (id: number) => Promise<void>;
  fetchMessages: (id: number, silent?: boolean) => Promise<void>;
  loadOlderMessages: (id: number) => Promise<void>;
  markRead: (id: number) => Promise<void>;
  setStatus: (id: number, status: TInboxConversationStatus) => Promise<void>;
  assign: (id: number, userId: number | null) => Promise<void>;
  /** Send a reply; it shows at once as pending, then as the stored message. */
  sendReply: (id: number, reply: { body?: string; image?: File }) => Promise<void>;
  retryPending: (id: number, pendingId: number) => Promise<void>;
  dismissPending: (id: number, pendingId: number) => void;
  fetchUnreadCount: () => Promise<void>;
};

const replaceIn = (list: TInboxConversation[], next: TInboxConversation) =>
  list.map((c) => (c.id === next.id ? next : c));

let nextPendingId = -1;

export const useInboxStore = create<InboxState & InboxActions>()(
  devtools(
    (set, get) => {
      /** Put a fresh copy of one conversation wherever the store holds it. */
      const upsertConversation = (next: TInboxConversation) =>
        set(
          (s) => ({
            conversations: replaceIn(s.conversations, next),
            extra: s.extra[next.id] ? { ...s.extra, [next.id]: next } : s.extra,
          }),
          false,
          "upsertConversation"
        );

      const setPending = (id: number, update: (list: TPendingReply[]) => TPendingReply[]) =>
        set((s) => ({ pending: { ...s.pending, [id]: update(s.pending[id] ?? []) } }), false, "setPending");

      /** Deliver one pending reply; on success it becomes a stored message in the thread. */
      const deliver = async (id: number, reply: TPendingReply) => {
        try {
          const message = reply.image
            ? await sendImageReply(id, reply.image.file, reply.body)
            : await sendTextReply(id, reply.body ?? "");
          if (reply.image) URL.revokeObjectURL(reply.image.previewUrl);
          set(
            (s) => {
              const thread = s.threads[id];
              const messages = thread ? [...thread.messages.filter((m) => m.id !== message.id), message] : [message];
              const summary = (c: TInboxConversation): TInboxConversation =>
                c.id === id && message.status !== "failed"
                  ? {
                      ...c,
                      lastMessageAt: message.sentAt,
                      lastMessageDirection: "out",
                      lastMessagePreview: message.body ?? "📷 Photo",
                    }
                  : c;
              return {
                threads: { ...s.threads, [id]: { messages, hasMore: thread?.hasMore ?? false, status: "IDLE" } },
                pending: { ...s.pending, [id]: (s.pending[id] ?? []).filter((p) => p.id !== reply.id) },
                conversations: s.conversations.map(summary),
                extra: s.extra[id] ? { ...s.extra, [id]: summary(s.extra[id]) } : s.extra,
              };
            },
            false,
            "sendReply/fulfilled"
          );
        } catch (e) {
          setPending(id, (list) =>
            list.map((p) => (p.id === reply.id ? { ...p, status: "failed", error: getErrorFromBackend(e) } : p))
          );
        }
      };

      return {
        conversations: [],
        listStatus: "IDLE",
        initialized: false,
        filters: { showResolved: false, channel: "all", assigned: "all", search: "" },
        extra: {},
        threads: {},
        pending: {},
        unreadCount: 0,
        unassignedCount: 0,

        setActive: (id) => set({ activeId: id }, false, "setActive"),

        setFilters: (patch) => {
          set((s) => ({ filters: { ...s.filters, ...patch } }), false, "setFilters");
          // A failure leaves listStatus REJECTED, which the list shows with a Retry.
          get().fetchConversations().catch(() => undefined);
        },

        fetchConversations: async (silent = false) => {
          if (!silent) set({ listStatus: "PENDING" }, false, "fetchConversations/pending");
          try {
            const { conversations, nextCursor } = await getConversations(get().filters);
            set(
              (s) => {
                // The open thread stays open when the new page no longer lists it
                // (resolved with resolved hidden, loaded via "Load more", reassigned).
                const open = s.activeId === undefined ? undefined : s.conversations.find((c) => c.id === s.activeId);
                const dropped = open && !conversations.some((c) => c.id === open.id);
                return {
                  conversations,
                  nextCursor,
                  listStatus: "IDLE",
                  initialized: true,
                  extra: dropped ? { ...s.extra, [open.id]: open } : s.extra,
                };
              },
              false,
              "fetchConversations/fulfilled"
            );
          } catch (e) {
            set({ listStatus: "REJECTED", initialized: true }, false, "fetchConversations/rejected");
            if (!silent) throw e;
          }
        },

        loadMoreConversations: async () => {
          const { nextCursor, filters } = get();
          if (!nextCursor) return;
          const page = await getConversations(filters, nextCursor);
          set(
            (s) => ({ conversations: [...s.conversations, ...page.conversations], nextCursor: page.nextCursor }),
            false,
            "loadMoreConversations"
          );
        },

        ensureConversation: async (id) => {
          const { conversations, extra } = get();
          if (conversations.some((c) => c.id === id) || extra[id]) return;
          const conversation = await getConversation(id);
          set((s) => ({ extra: { ...s.extra, [id]: conversation } }), false, "ensureConversation");
        },

        fetchMessages: async (id, silent = false) => {
          const current = get().threads[id];
          if (!silent || !current) {
            set(
              (s) => ({ threads: { ...s.threads, [id]: { messages: current?.messages ?? [], hasMore: false, status: "PENDING" } } }),
              false,
              "fetchMessages/pending"
            );
          }
          try {
            const { messages, hasMore } = await getMessages(id);
            // A silent refresh keeps older pages already loaded above the latest page.
            const kept = silent && current ? current.messages.filter((m) => m.id < (messages[0]?.id ?? 0)) : [];
            set(
              (s) => ({
                threads: {
                  ...s.threads,
                  [id]: { messages: [...kept, ...messages], hasMore: kept.length ? current!.hasMore : hasMore, status: "IDLE" },
                },
              }),
              false,
              "fetchMessages/fulfilled"
            );
          } catch (e) {
            set(
              (s) => ({ threads: { ...s.threads, [id]: { messages: current?.messages ?? [], hasMore: false, status: "REJECTED" } } }),
              false,
              "fetchMessages/rejected"
            );
            if (!silent) throw e;
          }
        },

        loadOlderMessages: async (id) => {
          const thread = get().threads[id];
          const oldest = thread?.messages[0];
          if (!thread?.hasMore || !oldest) return;
          const { messages, hasMore } = await getMessages(id, oldest.id);
          set(
            (s) => ({ threads: { ...s.threads, [id]: { messages: [...messages, ...thread.messages], hasMore, status: "IDLE" } } }),
            false,
            "loadOlderMessages"
          );
        },

        markRead: async (id) => {
          const updated = await markConversationRead(id);
          upsertConversation(updated);
          void get().fetchUnreadCount();
        },

        setStatus: async (id, status) => {
          const updated = await setConversationStatus(id, status);
          upsertConversation(updated);
          void get().fetchUnreadCount();
        },

        assign: async (id, userId) => {
          const updated = await assignConversation(id, userId);
          upsertConversation(updated);
          void get().fetchUnreadCount();
          // Leaving "Mine"/"Unassigned" views consistent with the new assignee.
          if (get().filters.assigned !== "all") void get().fetchConversations(true);
        },

        sendReply: async (id, { body, image }) => {
          const reply: TPendingReply = {
            id: nextPendingId--,
            body: body?.trim() || undefined,
            image: image ? { file: image, previewUrl: URL.createObjectURL(image) } : undefined,
            status: "sending",
            sentAt: new Date().toISOString(),
          };
          setPending(id, (list) => [...list, reply]);
          await deliver(id, reply);
        },

        retryPending: async (id, pendingId) => {
          const reply = get().pending[id]?.find((p) => p.id === pendingId);
          if (!reply) return;
          const retry: TPendingReply = { ...reply, status: "sending", error: undefined };
          setPending(id, (list) => list.map((p) => (p.id === pendingId ? retry : p)));
          await deliver(id, retry);
        },

        dismissPending: (id, pendingId) => {
          const reply = get().pending[id]?.find((p) => p.id === pendingId);
          if (reply?.image) URL.revokeObjectURL(reply.image.previewUrl);
          setPending(id, (list) => list.filter((p) => p.id !== pendingId));
        },

        fetchUnreadCount: async () => {
          try {
            const { count, unassigned } = await getUnreadCount();
            set({ unreadCount: count, unassignedCount: unassigned }, false, "fetchUnreadCount");
          } catch {
            // The badge is cosmetic; a failed refresh keeps the last count.
          }
        },
      };
    },
    { name: "inbox-store" }
  )
);

/** The conversation with this id, from the list or opened directly from a link. */
export const selectConversation = (id: number | undefined) => (s: InboxState) =>
  id === undefined ? undefined : s.conversations.find((c) => c.id === id) ?? s.extra[id];
